import { shallowEqual } from "react-redux"

import { WebApp } from "./constants"
import {
  type BottomButton as TelegramBottomButton,
  type BottomButtonType as TelegramBottomButtonType
  // type BottomButtonParams as TelegramBottomButtonParams
} from "./types"

type AtLeastOne<T> = {
  [K in keyof T]: Pick<T, K>
}[keyof T] &
  Partial<T>

type TelegramBottomButtonOptions = Pick<
  TelegramBottomButton,
  "type" | "text" | "color" | "textColor" | "hasShineEffect" | "isVisible" | "isActive" | "position"
  // | "isProgressVisible"
>

const DEFAULT_CONFIG: Record<
  TelegramBottomButtonType,
  { button: TelegramBottomButton; params: Omit<TelegramBottomButtonOptions, "type"> }
> = {
  main: {
    button: WebApp.MainButton,
    params: {
      text: "Save",
      color: "#EAB308",
      textColor: "#111827",
      hasShineEffect: true,
      position: "right",
      isActive: true,
      isVisible: false
    }
  },
  secondary: {
    button: WebApp.SecondaryButton,
    params: {
      text: "Reset",
      color: "#374151",
      textColor: "#D1D5DB",
      hasShineEffect: true,
      position: "left",
      isActive: true,
      isVisible: false
    }
  }
}

export class BottomButton {
  private button: TelegramBottomButton
  private onClickHandler?: () => void
  // private params: Omit<TelegramBottomButtonOptions, "type">

  constructor(type: TelegramBottomButtonType) {
    this.button = DEFAULT_CONFIG[type].button
    // this.params = {
    //   text: this.button.text,
    //   color: this.button.color,
    //   textColor: this.button.textColor,
    //   hasShineEffect: this.button.hasShineEffect,
    //   position: this.button.position,
    //   isActive: this.button.isActive,
    //   isVisible: this.button.isVisible
    // }

    this.setParams(DEFAULT_CONFIG[type].params)
  }

  public setParams(params: AtLeastOne<TelegramBottomButtonOptions>) {
    if (shallowEqual(params, this.params)) return

    // Use nullish coalescing (??) for booleans to properly handle false values
    this.button.setParams({
      text: params.text ?? this.params.text,
      color: params.color ?? this.params.color,
      text_color: params.textColor ?? this.params.textColor,
      has_shine_effect: params.hasShineEffect ?? this.params.hasShineEffect,
      position: params.position ?? this.params.position,
      is_active: params.isActive ?? this.params.isActive,
      is_visible: params.isVisible ?? this.params.isVisible
    })
  }

  public setText(text: string) {
    if (text === this.params.text) return

    this.button.setText(text)
    // this.params.text = text

    return this
  }

  public onClick(handler: () => void) {
    if (this.onClickHandler === handler) return

    this.offClick()
    this.onClickHandler = handler
    this.button.onClick(handler)

    return this
  }

  /**
   * Releases the click slot. Pass the handler you registered so a consumer that is
   * tearing down cannot unregister a handler another consumer has since claimed;
   * calling it without an argument still clears whatever is registered.
   */
  public offClick(handler?: () => void) {
    if (!this.onClickHandler) return
    if (handler && handler !== this.onClickHandler) return

    this.button.offClick(this.onClickHandler)
    this.onClickHandler = undefined
  }

  public show() {
    if (this.button.isVisible) return

    this.button.show()
  }

  public hide() {
    this.hideProgress()
    this.button.hide()
  }

  /**
   * Configure and show button with handler in one call.
   * Re-registers handler to fix issue where parent's buildCTA removes handlers.
   */
  public configure(text: string, handler: () => void) {
    this.setText(text)
    this.onClick(handler)
    this.show()
    this.enable()
    return this
  }

  public enable() {
    this.button.enable()
  }

  public disable() {
    this.button.disable()
  }

  public showProgress(leaveActive = false) {
    this.button.showProgress(leaveActive)
  }

  public hideProgress() {
    this.button.hideProgress()
  }

  public toggleActive() {
    if (this.button.isActive) {
      this.disable()
    } else {
      this.enable()
    }
  }

  public toggleVisibility() {
    if (this.button.isVisible) {
      this.hide()
    } else {
      this.show()
    }
  }

  public get params() {
    return {
      text: this.button.text,
      color: this.button.color,
      textColor: this.button.textColor,
      hasShineEffect: this.button.hasShineEffect,
      position: this.button.position,
      isActive: this.button.isActive,
      isVisible: this.button.isVisible
    }
  }

  // public get type() {
  //   return this.button.type
  // }

  // public get text() {
  //   return this.button.text
  // }

  // public get textColor() {
  //   return this.button.textColor
  // }
}

export const TelegramMainButton = new BottomButton("main")
export const TelegramSecondaryButton = new BottomButton("secondary")
